import styles from './styles.module.css';
import { Form, Input } from 'antd';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFujiAuth } from '../../../auth/useFujiAuth';
import { useLocation, useNavigate } from 'react-router-dom';
import { isEmptyObject, showNotificationDialog } from '../../../utils';
import Header from '../../../components/header/Header';
import CenterLoading from '../../../components/CenterLoading/CenterLoading';
import ButtonNetwork from '../../../utils/network-connect/ButtonNetwork';
import ConfirmCodeDescription from '../../../components/confirm-code-desscription/ConfirmCodeDescription';
import isOnline from '../../../utils/network-connect/NetworkConnect';
import { IS_IN_PURCHASE, localStorageKeys } from '../../../utils/Constants';

/**
 * 図14-2 メールアドレス変更確認コード入力画面
 */
const ChangeEmailConfirmScreen = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const [seconds, setSeconds] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const email = location.state?.email;
    const { verifyEmailValidationCode, updateUserAttributes } = useFujiAuth();

    useEffect(() => {
        if (!location.state?.email) {
            navigate('/auth');
        }
    });
    /**
     * 確認ボタンを押下の処理
     *
     * @param {String} code
     */
    const onFinish = ({ code }: { code: string }) => {
        //ローディングを表示
        setIsLoading(true);
        verifyEmailValidationCode(code, ({ isSuccessed, message }: { isSuccessed: boolean; message: any }) => {
            //ローディングを非表示
            setIsLoading(false);
            if (isSuccessed) {
                //チケットページ遷移
                // ログインに成功して、画面遷移を行う
                const isInPurchase = localStorage.getItem(IS_IN_PURCHASE) !== null;
                const loginFlow = JSON.parse(localStorage.getItem(localStorageKeys.LOGIN_FLOW) ?? '{}');
                if (isInPurchase || !isEmptyObject(loginFlow)) {
                    // 購入中の場合は戻る。
                    navigate(loginFlow.path, { state: loginFlow.state });
                } else {
                    navigate('/profile', { replace: true });
                }
                // MSG.10-3 メールアドレス変更完了メッセージ
                showNotificationDialog(
                    'success',
                    'change_mail_successfully',
                    t('notification.title'),
                    t('notification.change-mail-success'),
                );
            } else if (message.name === 'CodeMismatchException') {
                // MSG.10-E3 検証失敗エラー
                showNotificationDialog(
                    'error',
                    'confirm-code-error',
                    t('notification.title'),
                    t('error-message.user-info.incorrect-code'),
                );
            } else if (
                message.name === 'AliasExistsException' &&
                message.message.includes('An account with the given email already exists')
            ) {
                // MSG.10-E1 登録済みメールアドレスエラー
                showNotificationDialog(
                    'error',
                    'confirm-code-error',
                    t('notification.title'),
                    t('error-message.user-info.already-registered-email'),
                );
            } else if (message.message.includes('Network error')) {
                showNotificationDialog('error', '', t('notification.title'), t('error-message.server-error'));
            } else {
                // MSG.10-E2 メールアドレス更新失敗エラー
                showNotificationDialog(
                    'error',
                    'confirm-code-error',
                    t('notification.title'),
                    t('error-message.user-info.change-info-failed'),
                );
            }
        });
    };
    const onFinishFailed = (error: any) => {
        console.log('error ', error);
    };

    /**
     * 確認コードを再送信の実行
     */
    const resendConfirmCode = () => {
        if (isOnline()) {
            //ローディングを表示
            setIsLoading(true);
            updateUserAttributes({ email: email }, ({ isSuccessed }: { isSuccessed: boolean }) => {
                //ローディングを非表示
                setIsLoading(false);
                if (isSuccessed) {
                    showNotificationDialog(
                        'success',
                        'resend-confirm-code',
                        t('notification.title'),
                        t('notification.resend-confirm-code'),
                    );
                    if (seconds === 0) {
                        setSeconds(60);
                        const timeInterval = setInterval(() => {
                            setSeconds((second) => {
                                const number = second - 1;
                                if (number === 0) {
                                    clearInterval(timeInterval);
                                }
                                return number;
                            });
                        }, 1000);
                    }
                }
            });
        } else {
            showNotificationDialog('error', '', t('notification.title'), t('error-message.server-error'));
        }
    };
    return (
        <React.Fragment>
            {isLoading && <CenterLoading overFooter={true} />}
            <Header title={t('header.confirm-code')} />
            <div className={`content-footer-container scrollable ${styles.changeMailContainer}`}>
                <div className={styles.changeMailContent}>
                    <ConfirmCodeDescription email={email} />
                    <button
                        className={styles.btnResendCode}
                        style={{ opacity: seconds > 0 ? 0.6 : 1 }}
                        disabled={seconds > 0 ? true : false}
                        onClick={resendConfirmCode}
                    >
                        {t('confirm-code.resend-code')} {seconds > 0 && <span>({seconds})</span>}
                    </button>
                    <Form
                        className={styles.confirmForm}
                        initialValues={{
                            remember: true,
                        }}
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                    >
                        <Form.Item
                            name="code"
                            rules={[
                                {
                                    required: true,
                                    message: t('validate.correct-code') ?? '',
                                },
                            ]}
                        >
                            <Input
                                style={{ borderRadius: 4, height: 50 }}
                                placeholder={t('form.confirm-code') ?? ''}
                                size="large"
                                maxLength={6}
                            />
                        </Form.Item>
                        <Form.Item>
                            <ButtonNetwork title={t('button.change')} type="submit" />
                        </Form.Item>
                    </Form>
                </div>
            </div>
        </React.Fragment>
    );
};

export default ChangeEmailConfirmScreen;
